import { CADDrawingUnitHelper } from "./CADDrawing/CADDrawingUnitHelper";
import { EdgeAdjustment, EdgeAdjustmentWithoutID, EdgeAdjustmentWithoutType } from "./Components/EdgeTools/AddEdgeAdjustment";
import { CADOperationTypeEnum, CADPlacementReferenceTypeEnum, EdgeAdjustmentsEnum, SegmentTypeEnum } from "./enums";
import { IArc, ICADEdge, ICADLayer, ICADModel, ICADOperation, ICADOperationPlacement, ICADOperationProperties, ICircle, IPoint, ISegment, IUniqueID } from "./interfaces";

enum HighlightModeEnum {
    None = 0,
    Normal = 1,
    AddingCutout = 2,
    CopyingCutout = 3,
    SelectingCutouts = 4,
    SelectingEdges = 5,
    NarrowNone = 6,
    ReadableText = 7,
}
export class CADDrawingHelpers {

    #canvasBounds: DOMRect;
    #ctx: CanvasRenderingContext2D;
    #unitHelper: CADDrawingUnitHelper;

    constructor(ctx: CanvasRenderingContext2D, canvasBounds: DOMRect, unitHelper: CADDrawingUnitHelper) {
        this.#canvasBounds = canvasBounds;
        this.#ctx = ctx;
        this.#unitHelper = unitHelper;
    }

    public ClearCanvas(): void {
        this.#ctx.clearRect(0, 0, this.#canvasBounds.width, this.#canvasBounds.height);
    }

    public DrawSelectedOperation(uniqueID: number | null, model: ICADModel | null, currentLayer: ICADLayer | null): void {

        this.DrawOperationInternal(CADDrawingHelpers.CADOperationFromUniqueID(uniqueID, model?.operations), currentLayer, HighlightModeEnum.SelectingCutouts);
    }

    public DrawHoveredOperation(uniqueID: number | null, model: ICADModel | null, currentLayer: ICADLayer | null): void {

        const op = CADDrawingHelpers.CADOperationFromUniqueID(uniqueID, model?.operations);

        if (op) {
            this.DrawOperationInternal(op, currentLayer, HighlightModeEnum.CopyingCutout);
        } else {
            const adj = CADDrawingHelpers.EdgeAdjustmentFromUniqueID(uniqueID, currentLayer);
            if (adj) {
                this.DrawEdge(currentLayer, adj.edgeName, HighlightModeEnum.CopyingCutout);
            }
        }
    }

    public DrawAddingEditingAdjustment(currentLayer: ICADLayer | null, adjustment: EdgeAdjustmentWithoutID | null) {

        if (!adjustment || !currentLayer) return;

        this.DrawEdge(currentLayer, adjustment.edgeName, HighlightModeEnum.SelectingCutouts);
    }

    public static CADOperationFromUniqueID(uniqueID: number | null, operations: ICADOperation[] | undefined): ICADOperation | null {

        if (!uniqueID) return null;

        if (!operations) return null;

        const op = operations.find(op => op.uniqueID === uniqueID);

        return op ?? null;

    }

    public static EdgeAdjustmentFromUniqueID(uniqueID: number | null, layer: ICADLayer | null): EdgeAdjustment | null {

        if (!uniqueID) return null;

        if (!layer) return null;

        let adjustment: EdgeAdjustment | null | undefined;

        let modelAdjustment: EdgeAdjustmentWithoutType & IUniqueID | null | undefined;

        modelAdjustment = layer.edgeElbows.find(adj => adj.uniqueID === uniqueID);
        if (modelAdjustment) {
            adjustment = { ...modelAdjustment, adjustmentType: EdgeAdjustmentsEnum.Elbow };
        } else {
            modelAdjustment = layer.edgeRakes.find(adj => adj.uniqueID === uniqueID);
            if (modelAdjustment) {
                adjustment = { ...modelAdjustment, adjustmentType: EdgeAdjustmentsEnum.Rake };
            } else {
                modelAdjustment = layer.edgeShifts.find(adj => adj.uniqueID === uniqueID);
                if (modelAdjustment) {
                    adjustment = { ...modelAdjustment, adjustmentType: EdgeAdjustmentsEnum.Shift };
                }
            }
        }

        return adjustment ?? null;

    }

    private DrawOperationInternal(op: ICADOperation | null, layer: ICADLayer | null, highlightMode: HighlightModeEnum): void {

        if (!op || !layer) return;

        //Set hovered line styles to be consistent with CORE
        this.#ctx.lineJoin = "round";
        this.#ctx.lineCap = "round";
        this.#ctx.strokeStyle = this.GetStrokeStyle(highlightMode);
        this.#ctx.lineWidth = this.GetStrokeWidth(highlightMode);

        if (op.operationType === CADOperationTypeEnum.EdgeProcessing) {
            const edgeworkEdges: ISegment[] = [];

            if (op.edgeName === "0" || op.edgeName === "") {
                for (const edge of layer.edges) {
                    edgeworkEdges.push(...edge.edgeSegments);
                }
                this.DrawRegion(edgeworkEdges, false);
            }
            else {
                const edge = layer.edges.find(e => e.edgeName === op.edgeName);
                if (edge) {
                    this.DrawRegion(edge.edgeSegments, false);
                }
            }

        } else {
            // draw the hovered operation  
            if (op.regions) {
                for (const region of op.regions) {
                    this.DrawRegion(region, (op.operationType === CADOperationTypeEnum.InternalCutout));
                }
            }
        }

    }

    private GetCornerPoint(edge: ICADEdge, fromStart: boolean): IPoint {
        if (fromStart) {
            return edge.startPoint;
        } else {
            return edge.endPoint;
        }
    }

    private DrawCornerMarker(cornerPoint: IPoint, highlightMode: HighlightModeEnum) {

        const radius = this.#unitHelper.ConvertCanvasDistanceToModelSpace(5);

        const circle: ICircle = { type: SegmentTypeEnum.Circle, centerPoint: cornerPoint, radius: radius, startPoint: cornerPoint, endPoint: cornerPoint, side: 0 };
        this.#ctx.strokeStyle = "red"
        this.#ctx.lineWidth = 2;
        this.DrawRegion([circle], false);

        this.#ctx.strokeStyle = this.GetStrokeStyle(highlightMode);
        this.#ctx.lineWidth = this.GetStrokeWidth(highlightMode);
        this.DrawRegion([circle], false);

    }


    private DrawCrosshair(centerPoint: IPoint, radius: number, highlightMode: HighlightModeEnum) {

        this.#ctx.strokeStyle = "black"
        this.#ctx.lineWidth = 1;

        const xyCircle: ICircle = { type: SegmentTypeEnum.Circle, centerPoint: centerPoint, radius: radius, startPoint: centerPoint, endPoint: centerPoint, side: 0 };
        const crossHair = [
            { type: SegmentTypeEnum.Line, startPoint: { x: centerPoint.x - (radius / 2), y: centerPoint.y }, endPoint: { x: centerPoint.x + (radius / 2), y: centerPoint.y }, side: 0 },
            { type: SegmentTypeEnum.Line, startPoint: { x: centerPoint.x, y: centerPoint.y - (radius / 2) }, endPoint: { x: centerPoint.x, y: centerPoint.y + (radius / 2) }, side: 0 }
        ]

        this.DrawRegion([xyCircle], false);
        this.DrawRegion(crossHair, false);

        this.#ctx.strokeStyle = this.GetStrokeStyle(highlightMode);
        this.#ctx.lineWidth = this.GetStrokeWidth(highlightMode);
        this.DrawRegion([xyCircle], false);

    }

    private DrawEdge(layer: ICADLayer | null, edgeName: string, highlightMode: HighlightModeEnum) {

        if (!edgeName) return;

        if (!layer) return;

        const edge = layer.edges.find(e => e.edgeName === edgeName);

        if (edge) {
            this.#ctx.lineJoin = "round";
            this.#ctx.lineCap = "round";
            this.#ctx.strokeStyle = this.GetStrokeStyle(highlightMode);
            this.#ctx.lineWidth = this.GetStrokeWidth(highlightMode);
            this.DrawRegion(edge.edgeSegments, false);
        }

    }

    public DrawAddingOperation(placement: ICADOperationPlacement | undefined, operation: ICADOperationProperties | undefined, model: ICADModel | null, layer: ICADLayer | null) {

        if (!placement || !operation) return;

        if (!model) return;

        if (!layer) return;

        let edge: ICADEdge | undefined = undefined;

        this.#ctx.lineJoin = "round";
        this.#ctx.lineCap = "round";
        this.#ctx.strokeStyle = this.GetStrokeStyle(HighlightModeEnum.AddingCutout);
        this.#ctx.lineWidth = this.GetStrokeWidth(HighlightModeEnum.AddingCutout);

        switch (operation.placementReferenceType) {
            case CADPlacementReferenceTypeEnum.Edge:

                edge = layer.edges.find(e => e.edgeName === placement.edgeName);
                if (edge) {
                    this.DrawRegion(edge.edgeSegments, false);
                }

                break;

            case CADPlacementReferenceTypeEnum.Corner:

                edge = layer.edges.find(e => e.edgeName === placement.edgeName);
                if (edge) {
                    const cornerPoint = this.GetCornerPoint(edge, placement.fromStart);
                    this.DrawCornerMarker(cornerPoint, HighlightModeEnum.AddingCutout);
                }

                break;

            case CADPlacementReferenceTypeEnum.CornerAndXY:

                edge = layer.edges.find(e => e.edgeName === placement.edgeName);
                if (edge) {
                    const cornerPoint = this.GetCornerPoint(edge, placement.fromStart);
                    this.DrawCornerMarker(cornerPoint, HighlightModeEnum.AddingCutout);

                    //Future: this must use relative coordinates based on the corner being measure from
                    const dx = parseFloat(operation.x) * (operation.xPositive ? 1 : -1);
                    const dy = parseFloat(operation.y) * (operation.yPositive ? 1 : -1);

                    if (!isNaN(dx) && !isNaN(dy)) {
                        const xyPoint: IPoint = { x: cornerPoint.x + dx, y: cornerPoint.y + dy };
                        this.DrawCrosshair(xyPoint, this.#unitHelper.ConvertCanvasDistanceToModelSpace(10), HighlightModeEnum.AddingCutout);
                    }
                }

                break;

            case CADPlacementReferenceTypeEnum.EdgeAndDistanceFromCorner:
                edge = layer.edges.find(e => e.edgeName === placement.edgeName);
                if (edge) {
                    this.DrawRegion(edge.edgeSegments, false);

                    const cornerPoint = this.GetCornerPoint(edge, placement.fromStart);
                    this.DrawCornerMarker(cornerPoint, HighlightModeEnum.AddingCutout);
                }

                break;
        }

    }


    private DrawRegion(region: ISegment[], closeRegion: boolean): void {
        // draw the region

        if (region.length === 0) return;

        if (region.length === 1 && region[0].type === SegmentTypeEnum.Circle) {

            const circle = region[0] as ICircle;
            const centerPoint = this.#unitHelper.ConvertModelPointToCanvasSpace(circle.centerPoint);
            const radius = this.#unitHelper.ConvertModelDistanceToCanvasSpace(circle.radius);

            this.#ctx.beginPath();
            this.#ctx.arc(centerPoint.x, centerPoint.y, radius, 0, 2 * Math.PI);

        } else if (region.length === 1 && region[0].type === SegmentTypeEnum.Arc) {

            const arc = region[0] as IArc;
            const centerPoint = this.#unitHelper.ConvertModelPointToCanvasSpace(arc.centerPoint);
            const radius = this.#unitHelper.ConvertModelDistanceToCanvasSpace(arc.radius);
            this.#ctx.beginPath();
            this.#ctx.arc(centerPoint.x, centerPoint.y, radius, arc.startAngle, arc.endAngle, arc.isClockwise);

        } else {

            let lastPoint: IPoint | null = null;

            this.#ctx.beginPath();

            for (let i = 0; i < region.length; i++) {
                const segment = region[i];
                if (lastPoint === null || lastPoint.x !== segment.startPoint.x || lastPoint.y !== segment.startPoint.y) {
                    const startPoint = this.#unitHelper.ConvertModelPointToCanvasSpace(segment.startPoint);
                    this.#ctx.moveTo(startPoint.x, startPoint.y);
                }

                if (segment.type === SegmentTypeEnum.Line) {

                    const endPoint = this.#unitHelper.ConvertModelPointToCanvasSpace(segment.endPoint);
                    this.#ctx.lineTo(endPoint.x, endPoint.y);

                } else if (segment.type === SegmentTypeEnum.Arc) {

                    const arc = segment as IArc;
                    const centerPoint = this.#unitHelper.ConvertModelPointToCanvasSpace(arc.centerPoint);
                    const radius = this.#unitHelper.ConvertModelDistanceToCanvasSpace(arc.radius);
                    this.#ctx.arc(centerPoint.x, centerPoint.y, radius, arc.startAngle, arc.endAngle, arc.isClockwise);

                }

                lastPoint = segment.endPoint;

            }

        }

        if (closeRegion) {
            this.#ctx.closePath();
        }

        this.#ctx.stroke();
    }

    private GetStrokeStyle(highlightMode: HighlightModeEnum): string {

        switch (highlightMode) {
            case HighlightModeEnum.None:
            case HighlightModeEnum.NarrowNone:
                return "rgba(0, 0, 0, 0)";

            case HighlightModeEnum.Normal:
            case HighlightModeEnum.SelectingCutouts:
            case HighlightModeEnum.SelectingEdges:
                return "rgba(0, 0, 255, 0.3921)"; //CORE uses a=100 which when divided by 255 is 0.3921

            case HighlightModeEnum.AddingCutout:
                return "rgba(255, 0, 0, 0.3137)"; //CORE uses a=80 which when divided by 255 is 0.3137

            case HighlightModeEnum.CopyingCutout:
                return "rgba(0, 255, 0, 0.3137)"; //CORE uses a=80 which when divided by 255 is 0.3137

            case HighlightModeEnum.ReadableText:
                return "rgba(255, 255, 2558, 1)";

        }

    }

    private GetStrokeWidth(highlightMode: HighlightModeEnum): number {

        let penWidth = 10;

        switch (highlightMode) {
            case HighlightModeEnum.AddingCutout:
            case HighlightModeEnum.CopyingCutout:
            case HighlightModeEnum.SelectingCutouts:
                penWidth = 15;
                break;
            case HighlightModeEnum.NarrowNone:
                penWidth = 5;
                break;
        }

        return this.#unitHelper.GetCanvasRatio() * penWidth; //taken from DrawingMethods.GetHighLightPen()
    }


    public MousePositionInModelSpace(e: React.MouseEvent<HTMLCanvasElement>): IPoint {
        const canvasBounds = e.currentTarget.getBoundingClientRect();

        // Get mouse position in canvas space
        const canvasX = (e.clientX - canvasBounds.left);
        let canvasY = (e.clientY - canvasBounds.top);

        // Mouse Y coordinate is positive downwards, so we need to flip it 
        // (The canvas handles drawing this by applying an offset and transform to the entire canvas)
        canvasY = canvasBounds.height - canvasY;

        return this.#unitHelper.ConvertCanvasPointToModelSpace({ x: canvasX, y: canvasY });
    }

}
